import { EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT } from '@zaoblako/shared/modules/eav/constants/eav-attribute-types-excluded-in-personal-account.constants';
import type { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';

import type { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import type { EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';

/** EAV helpers for personal account HelpDesk (filter admin-only attribute types, merge values). Singleton. */
export class EavPersonalAccountHelperClass {
  private static instance: EavPersonalAccountHelperClass = null;

  /** Get helper instance. Create it if not exists. */
  public static getInstance(): EavPersonalAccountHelperClass {
    if (!EavPersonalAccountHelperClass.instance) {
      EavPersonalAccountHelperClass.instance = new EavPersonalAccountHelperClass();
    }
    return EavPersonalAccountHelperClass.instance;
  }

  /** Whether attribute type is editable in personal account HelpDesk */
  public isAttributeTypeAllowed(type: EavAttributeValueTypeEnum): boolean {
    return !EAV_ATTRIBUTE_TYPES_EXCLUDED_IN_PERSONAL_ACCOUNT.includes(type);
  }

  /** Filter attributes visible in personal account */
  public filterAttributes(attributes?: EavAttributeFindInterface[]): EavAttributeFindInterface[] {
    return (attributes || []).filter(
      ({ type, showInPersonalAccount }) =>
        showInPersonalAccount === true && this.isAttributeTypeAllowed(type),
    );
  }

  /** Filter entity type attributes for personal account API responses */
  public filterEntityType(entityType?: EavEntityTypeFindInterface): EavEntityTypeFindInterface | undefined {
    if (!entityType) {
      return entityType;
    }
    return {
      ...entityType,
      attributes: this.filterAttributes(entityType.attributes),
    };
  }

  /** Filter task type entity type for personal account */
  public filterHelpdeskTaskType(taskType?: HelpdeskTaskTypeFindInterface): HelpdeskTaskTypeFindInterface | undefined {
    if (!taskType) {
      return taskType;
    }
    return {
      ...taskType,
      entityType: this.filterEntityType(taskType.entityType),
    };
  }

  /** Keep only values for allowed attribute ids */
  public pickValues(
    values: Record<number, any> | undefined,
    allowedAttributes: EavAttributeFindInterface[],
  ): Record<number, any> {
    const allowedIds = new Set(this.filterAttributes(allowedAttributes).map(({ id }) => id));
    const result: Record<number, any> = {};
    for (const [key, value] of Object.entries(values || {})) {
      const id = parseInt(key, 10);
      if (allowedIds.has(id)) {
        result[id] = value;
      }
    }
    return result;
  }

  /**
   * Merge incoming personal-account EAV patch with existing values.
   * Allowed attributes are taken from incoming; admin-only attributes stay from existing.
   */
  public mergeValues(
    existingValues: Record<number, any> | undefined,
    incomingValues: Record<number, any> | undefined,
    allAttributes: EavAttributeFindInterface[],
  ): Record<number, any> {
    const allowed = this.filterAttributes(allAttributes);
    const allowedIds = new Set(allowed.map(({ id }) => id));
    const merged: Record<number, any> = { ...(existingValues || {}) };

    for (const { id } of allowed) {
      if (incomingValues && id in incomingValues) {
        merged[id] = incomingValues[id];
      }
    }

    for (const attribute of allAttributes || []) {
      if (!allowedIds.has(attribute.id) && existingValues && attribute.id in existingValues) {
        merged[attribute.id] = existingValues[attribute.id];
      }
    }

    return merged;
  }
}
